import { useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { getCustomizePage } from "services/get-customizePage";
import { useSetRecoilState, useRecoilState } from "recoil";
import { browseBannerState, cartState, toastState } from "atoms";
import { BrowseBannerModel } from "atoms/models";
import { useNavigate, useLocation } from "react-router-dom";
import { useWrapRequest } from "utils";
import { postRemoveFromCart } from "services/post-removeFromCart";
import { getCart } from "services/get-getCart";
import { mobileRegistryModalState } from "atoms";
import { PATH } from "utils/const";

interface Product {
  productName: string;
  serviceLevelDescription: string;
  id: number;
}

const CartIcon = () => {
  const [cart, setCart] = useRecoilState(cartState);
  const wrapRequest = useWrapRequest();
  const [showPopover, setShowPopover] = useState(false);
  const navigate = useNavigate();
  const setBanner = useSetRecoilState<BrowseBannerModel>(browseBannerState);
  const [toast, setToast] = useRecoilState(toastState);
  const setShowMobileRegistryModal = useSetRecoilState(mobileRegistryModalState);
  const location = useLocation();

  const handleCustomizeFromCartIcon = async () => {
    wrapRequest(async () => {
      const response = await getCustomizePage();
      if (cart?.list?.cartProducts.length > 0 && response?._links?.self?.href === "/ecommerce/route/customizePage") {
        setBanner({} as BrowseBannerModel);
        navigate(PATH.CUSTOMIZE);
      }
    });
  };

  const handleRemoveFromCart = (productId: number) => {
    wrapRequest(async () => {
      const response = await postRemoveFromCart(productId, cart.bundle ? true : false, false);
      const newCart = await getCart();
      setCart(newCart);
      if (newCart.isCurrentCustomer) {
        navigate(PATH.CUSTOMER_SUMMARY);
      } else {
        navigate(PATH.BROWSE);
      }

      if (response.ecommerceAction === "error") {
        setToast({
          ...toast,
          show: true,
          variant: "danger",
          header: "Error",
          message: response?.message,
        });
      }
    });
  };

  const cartPopover = (
    <Popover id="popover-basic" className="cart-icon-popover">
      <div className="cart-tag">
        <span className="tag-border"></span>
        <span className="tag-mark"></span>
        <span className="tag-icon">
          <i className="fa-solid fa-cart-shopping"></i>
        </span>
      </div>
      <Popover.Body>
        <div className="title">
          <strong>MY CART</strong>
        </div>
        <hr />
        <div className="product-details">
          {cart?.list?.cartProducts.length > 0 ? (
            cart.list.cartProducts.map((product: Product, index: number) => {
              return (
                <div className="cart-line" key={index}>
                  <div className="description mb-4">
                    <div className="headline">
                      <strong>{product.productName}</strong>
                    </div>
                    <div className="product-name">{product.serviceLevelDescription}</div>
                  </div>
                  {!cart.bundle && cart.list.cartProducts.length > 0 ? (
                    <div className="remove-from-cart" onClick={() => handleRemoveFromCart(product.id)}>
                      Remove From Cart
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            <div className="text-center">Your cart is empty</div>
          )}
        </div>
        <hr />
        <div className="d-flex justify-content-center">
          <button className="btn btn-primary btn-sm popover-customize-button" onClick={() => handleCustomizeFromCartIcon()}>
            <strong>Customize Order</strong>
          </button>
        </div>
      </Popover.Body>
    </Popover>
  );

  const displayCartIcon = () => {
    if (location.pathname !== PATH.BROWSE && location.pathname !== PATH.CUSTOMER_SUMMARY) {
      return (
        <div className="cart-counter strong-bold" onClick={() => setShowMobileRegistryModal(true)}>
          <i className="fa-solid fa-cart-shopping me-1"></i> Cart ({" "}
          <span className="cart-counter-number">{!cart?.list?.cartProducts?.length ? 0 : cart?.list?.cartProducts?.length}</span> )
        </div>
      );
    } else if (location.pathname === PATH.BROWSE || location.pathname === PATH.CUSTOMER_SUMMARY) {
      return (
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          overlay={cartPopover}
          show={showPopover}
          onToggle={() => setShowPopover(!showPopover)}
          rootClose
        >
          <div
            className={
              location.pathname === PATH.BROWSE || location.pathname === PATH.CUSTOMER_SUMMARY
                ? "cart-counter-browse strong-bold"
                : "cart-counter strong-bold"
            }
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fa-solid fa-cart-shopping me-1"></i> Cart ({" "}
            <span className="cart-counter-number">{!cart?.list?.cartProducts?.length ? 0 : cart?.list?.cartProducts?.length}</span> )
          </div>
        </OverlayTrigger>
      );
    } else {
      return null;
    }
  };

  return <>{displayCartIcon()}</>;
};

export default CartIcon;
